import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Sparkles,
  TrendingUp,
  FileText,
  Target,
  MessageCircle,
  Loader2,
  Send,
  Lightbulb,
  GraduationCap,
  Award
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CareerAdvicePage() {
  const { token, isAuthenticated } = useAuth();
  const [question, setQuestion] = useState('');
  const [advice, setAdvice] = useState('');
  const [loading, setLoading] = useState(false);
  const [salaryData, setSalaryData] = useState(null);
  const [loadingSalary, setLoadingSalary] = useState(false);
  const [salaryForm, setSalaryForm] = useState({
    job_title: '',
    location: '',
    experience_years: 2
  });

  const DEFAULT_TOPICS = [
    { title: 'CV Writing Tips', description: 'Learn how to create an ATS-friendly CV' },
    { title: 'Interview Preparation', description: 'Ace your next job interview' },
    { title: 'Career Growth', description: 'Strategies for advancing your career' },
    { title: 'Skills Development', description: 'In-demand skills for 2026' },
  ];
  const DEFAULT_TIPS = [
    'Tailor your CV for every role you apply to',
    'Use the STAR method for behavioural interview questions',
    'Research the company thoroughly before interviews',
    'Build your professional network on LinkedIn',
    'Keep upskilling — take one new course per quarter',
  ];
  const TOPIC_ICONS = [FileText, Target, TrendingUp, Award, GraduationCap, MessageCircle, Lightbulb, Sparkles];

  const [careerTopics, setCareerTopics] = useState(DEFAULT_TOPICS);
  const [careerTips, setCareerTips] = useState(DEFAULT_TIPS);

  useEffect(() => {
    axios.get(`${API_URL}/api/content/career_topics`).then(r => { if (r.data?.items?.length) setCareerTopics(r.data.items); }).catch(()=>{});
    axios.get(`${API_URL}/api/content/career_tips`).then(r => { if (r.data?.items?.length) setCareerTips(r.data.items); }).catch(()=>{});
  }, []);

  const askCareerAdvice = async (e) => {
    e.preventDefault();
    if (!question.trim()) return;
    if (question.trim().length < 3) {
      toast.error('Please enter at least 3 characters');
      return;
    }

    if (!isAuthenticated) {
      toast.error('Please log in to get personalized advice');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/ai/career-advice?question=${encodeURIComponent(question.trim())}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const detail = Array.isArray(data.detail) ? data.detail.map(d => d.msg).join(', ') : data.detail;
        toast.error(detail || 'Failed to get advice');
        return;
      }
      setAdvice(data.advice);
    } catch (error) {
      toast.error('Failed to get advice');
    } finally {
      setLoading(false);
    }
  };

  const checkSalary = async (e) => {
    e.preventDefault();
    if (!salaryForm.job_title || !salaryForm.location) return;

    setLoadingSalary(true);
    try {
      const params = new URLSearchParams({
        job_title: salaryForm.job_title,
        location: salaryForm.location,
        experience_years: salaryForm.experience_years
      });
      const response = await fetch(`${API_URL}/api/ai/salary-benchmark?${params}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const detail = Array.isArray(data.detail) ? data.detail.map(d => d.msg).join(', ') : data.detail;
        toast.error(detail || 'Failed to get salary data');
        return;
      }
      setSalaryData(data);
    } catch (error) {
      toast.error('Failed to get salary data');
    } finally {
      setLoadingSalary(false);
    }
  };

  const formatSalary = (num) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      maximumFractionDigits: 0
    }).format(num);
  };

  return (
    <div className="min-h-screen bg-[#F0F4F8]" data-testid="career-advice-page">
      {/* Hero */}
      <div className="bg-[#003366] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <Badge className="bg-white/10 text-white border-white/20 mb-4">
              <Sparkles className="w-3 h-3 mr-1" />
              AI-Powered Insights
            </Badge>
            <h1 className="text-4xl font-bold mb-4">Career Advice Centre</h1>
            <p className="text-white/80 text-lg">
              Get personalized career guidance, salary insights, and expert tips to advance your professional journey.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* AI Career Advisor */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-xl gradient-ai flex items-center justify-center">
                    <MessageCircle className="w-5 h-5 text-white" />
                  </div>
                  AI Career Advisor
                </CardTitle>
                <CardDescription>
                  Ask any career-related question and get personalized advice
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={askCareerAdvice} className="space-y-4">
                  <Textarea
                    placeholder="e.g. How can I transition from marketing to product management?"
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    rows={4}
                    data-testid="career-question-input"
                  />
                  <Button 
                    type="submit"
                    disabled={loading || !question.trim()}
                    className="bg-[#E11D48] hover:bg-[#E11D48]/90"
                    data-testid="ask-advice-btn"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Get Advice
                  </Button>
                </form>

                {advice && (
                  <div className="mt-6 p-4 bg-purple-50 rounded-xl border border-purple-200">
                    <div className="flex items-center gap-2 mb-3">
                      <Sparkles className="w-5 h-5 text-purple-600" />
                      <span className="font-medium text-purple-900">AI Response</span>
                    </div>
                    <p className="text-foreground whitespace-pre-wrap">{advice}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Topics */}
            <div>
              <h2 className="text-2xl font-bold mb-6">Popular Topics</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {careerTopics.map((topic, idx) => {
                  const Icon = TOPIC_ICONS[idx % TOPIC_ICONS.length];
                  return (
                    <Card key={idx} className="hover:shadow-lg transition-shadow cursor-pointer">
                      <CardContent className="p-6">
                        <div className="w-12 h-12 rounded-xl bg-[#003366]/10 flex items-center justify-center mb-4">
                          <Icon className="w-6 h-6 text-[#003366]" />
                        </div>
                        <h3 className="font-semibold mb-2">{topic.title}</h3>
                        <p className="text-sm text-muted-foreground">{topic.description}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Salary Checker */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <TrendingUp className="w-5 h-5 text-[#003366]" />
                  Salary Checker
                </CardTitle>
                <CardDescription>
                  Get AI-powered salary benchmarks
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={checkSalary} className="space-y-4">
                  <div className="space-y-2">
                    <Input
                      placeholder="Job Title"
                      value={salaryForm.job_title}
                      onChange={(e) => setSalaryForm({ ...salaryForm, job_title: e.target.value })}
                      data-testid="salary-job-title"
                    />
                  </div>
                  <div className="space-y-2">
                    <Input
                      placeholder="Location"
                      value={salaryForm.location}
                      onChange={(e) => setSalaryForm({ ...salaryForm, location: e.target.value })}
                      data-testid="salary-location"
                    />
                  </div>
                  <div className="space-y-2">
                    <Input
                      type="number"
                      placeholder="Years of Experience"
                      value={salaryForm.experience_years}
                      onChange={(e) => setSalaryForm({ ...salaryForm, experience_years: parseInt(e.target.value) || 0 })}
                      data-testid="salary-experience"
                    />
                  </div>
                  <Button 
                    type="submit"
                    disabled={loadingSalary}
                    className="w-full bg-[#003366] hover:bg-[#003366]/90"
                    data-testid="check-salary-btn"
                  >
                    {loadingSalary ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : null}
                    Check Salary
                  </Button>
                </form>

                {salaryData && (
                  <div className="mt-6 space-y-4">
                    <div className="text-center p-4 bg-[#003366]/5 rounded-xl">
                      <p className="text-sm text-muted-foreground mb-1">Expected Range</p>
                      <p className="text-2xl font-bold text-[#003366]">
                        {formatSalary(salaryData.min_salary)} - {formatSalary(salaryData.max_salary)}
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Median: {formatSalary(salaryData.median_salary)}
                      </p>
                    </div>
                    {salaryData.insights && (
                      <div className="space-y-2">
                        <p className="text-sm font-medium">Insights</p>
                        <ul className="space-y-1">
                          {salaryData.insights.map((insight, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                              <Lightbulb className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                              <span>{insight}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Tips */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Quick Tips</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {careerTips.map((tip, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <GraduationCap className="w-4 h-4 text-[#003366] mt-0.5 flex-shrink-0" />
                      <span>{tip}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
